/**
 * SearchDrawer — extends MenuDrawer for the search-specific drawer behavior.
 * - Overrides button binding so form buttons (reset, predictive search) don't close the drawer.
 * - Focuses the search input when the drawer opens.
 * - Handles the close button + backdrop click.
 */
class SearchDrawer extends MenuDrawer {
  constructor() {
    super();
    this.searchInput = this.querySelector('input[type="search"]');
    this.defaultSuggestions = this.querySelector('[data-default-suggestions]');
    this.patchPredictiveSearch();
    this.initSuggestionsToggle();
  }

  patchPredictiveSearch() {
    const predictiveSearch = this.querySelector('predictive-search');
    if (!predictiveSearch) return;

    // Disable onFocusOut entirely — results stay open while the drawer is open.
    // The drawer itself controls visibility; closing the drawer hides everything.
    predictiveSearch.onFocusOut = function () {};

    // Observe DOM changes in the results container to highlight matching terms
    const resultsContainer = predictiveSearch.querySelector('[data-predictive-search]');
    if (resultsContainer) {
      const observer = new MutationObserver(() => {
        if (this._highlightRaf) return;
        this._highlightRaf = requestAnimationFrame(() => {
          this.highlightSearchTerms();
          this._highlightRaf = null;
        });
      });
      observer.observe(resultsContainer, { childList: true });
    }
  }

  initSuggestionsToggle() {
    if (!this.searchInput || !this.defaultSuggestions) return;

    const predictiveSearch = this.querySelector('predictive-search');

    // Listen to input changes
    this.searchInput.addEventListener('input', () => this.toggleSuggestions());

    // Handle the reset button clearing the input
    const form = this.searchInput.closest('form');
    if (form) {
      form.addEventListener('reset', () => {
        setTimeout(() => this.toggleSuggestions(), 0);
      });
    }

    // Observe the predictive-search open/results attributes
    if (predictiveSearch) {
      const attrObserver = new MutationObserver(() => this.toggleSuggestions());
      attrObserver.observe(predictiveSearch, { attributes: true, attributeFilter: ['open', 'results'] });
    }
  }

  toggleSuggestions() {
    const hasValue = this.searchInput.value.trim().length > 0;
    const predictiveSearch = this.querySelector('predictive-search');
    const isSearchOpen = predictiveSearch?.hasAttribute('open');

    const shouldHide = hasValue || isSearchOpen;
    this.defaultSuggestions.style.display = shouldHide ? 'none' : '';

    // Strip any leftover <mark> tags from default suggestions when they reappear
    if (!shouldHide) {
      this.defaultSuggestions.querySelectorAll('mark').forEach((mark) => {
        mark.replaceWith(mark.textContent);
      });
    }
  }

  /**
   * Normalize a string by removing diacritics (tildes) for accent-insensitive matching.
   */
  static normalize(str) {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  highlightSearchTerms() {
    const term = this.searchInput?.value?.trim();
    if (!term) return;

    const normTerm = SearchDrawer.normalize(term);
    const escaped = normTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'gi');

    // Only highlight inside predictive search results, not default suggestions
    const container = this.querySelector('[data-predictive-search]');
    if (!container) return;
    const headings = container.querySelectorAll('.predictive-search__item-heading');

    headings.forEach((heading) => {
      if (heading.querySelector('mark')) return;
      const original = heading.textContent;
      const normalized = SearchDrawer.normalize(original);

      // Build result preserving original characters but wrapping matches in <mark>
      let result = '';
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(normalized)) !== null) {
        result += this.escapeHtml(original.slice(lastIndex, match.index));
        result += '<mark>' + this.escapeHtml(original.slice(match.index, match.index + match[0].length)) + '</mark>';
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex > 0) {
        result += this.escapeHtml(original.slice(lastIndex));
        heading.innerHTML = result;
      }
    });
  }

  escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );

    // Only bind close behavior to the explicit close button
    this.querySelectorAll('.search-drawer__close-btn').forEach((button) =>
      button.addEventListener('click', this.onDrawerClose.bind(this))
    );
  }

  onDrawerClose(event) {
    event.preventDefault();
    const summary = this.mainDetailsToggle.querySelector('summary');
    this.closeMenuDrawer(event, summary);
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    // Focus the search input after the drawer animation
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 100);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent && elementToFocus) {
      elementToFocus.setAttribute('aria-expanded', false);
    }
  }

  // Close on backdrop click (click on summary::before pseudo-element area)
  onFocusOut() {
    // Do not close on focusout — the predictive search needs to keep focus management
  }
}

customElements.define('search-drawer', SearchDrawer);
